/**
 * @Description 标签选择弹窗公共组件
 * 支持多业务场景复用，通过 extraParams 透传业务实体参数
 */

import { FC, useEffect, useState } from 'react';
import { Form, Modal } from 'antd';
import { useMethods } from '@lhb/hook';
import { isArray } from '@lhb/func';
import { useBigdataFn } from '@/common/utils/bigdata';
import { getLabelGroupData, updateFranchiseeLabels } from '@/common/api/labels';
import { saveLabelRelations } from '@/common/api/networkplan';
import styles from './index.module.less';
import V2Form from '@/common/components/Form/V2Form';
import V2Empty from '@/common/components/Data/V2Empty';
import V2FormRadio from '@/common/components/Form/V2FormRadio/V2FormRadio';
import V2FormCheckbox from '@/common/components/Form/V2FormCheckbox/V2FormCheckbox';

interface SelectLabelModalProps {
  modalData: {
    open: boolean;
    /** 业务实体参数，如 { franchiseeId: 123 }，会合并到接口入参中 */
    extraParams: Record<string, any>;
  };
  /** 已选标签，用于表单回显 */
  showLabels: any[];
  type?: number; // 1. 加盟商 2.商圈
  onSuccess: () => void;
  onClose: () => void;
}

const SelectLabelModal: FC<SelectLabelModalProps> = ({
  modalData,
  showLabels,
  type = 1, // 默认加盟商
  onSuccess,
  onClose,
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook

  const [form] = Form.useForm();
  const [labelsData, setLabelsData] = useState<any[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);

  // 组件挂载时拉取全量标签分组数据
  useEffect(() => {
    if (!modalData.open) return;
    if (labelsData.length) return;
    methods.getSelections();
  }, [modalData.open]);

  // 弹窗打开且有已选标签时，将已选标签回显到表单
  useEffect(() => {
    if (modalData.open && showLabels?.length && labelsData?.length) {
      const formFields: any = {};
      // 遍历全量标签分组，将已选标签映射到对应的表单字段
      labelsData.forEach((labelsGroupItem: any) => {
        const { id, optionType, labels } = labelsGroupItem;
        showLabels.forEach((item: any) => {
          if (type === 1) { // 加盟商
            if (item.groupId === id) {
              if (optionType === 1) { // 单选：直接赋值
                formFields[`group-${id}`] = item.id;
              } else { // 多选：累加到数组
                formFields[`group-${id}`] = formFields[`group-${id}`] ? [...formFields[`group-${id}`], item.id] : [item.id];
              }
            }
          }
          if (type === 2) { // 商圈
            labels?.forEach((labelItem: any) => {
              if (labelItem.id === item.id) {
                if (optionType === 1) { // 单选：直接赋值
                  formFields[`group-${id}`] = item.id;
                } else { // 多选：累加到数组
                  formFields[`group-${id}`] = formFields[`group-${id}`] ? [...formFields[`group-${id}`], item.id] : [item.id];
                }
              }
            });
          }
        });
      });
      form.setFieldsValue(formFields);
    }
  }, [showLabels, labelsData, modalData.open]);

  const methods = useMethods({
    // 获取全量标签分组数据
    async getSelections() {
      try {
        const res: any = await getLabelGroupData({ businessType: type });
        setLabelsData(isArray(res) ? res : []);
      } catch (err) {
        console.log(err);
      } finally {
        setLoaded(true);
      }
    },
    handleModalClose() {
      form.resetFields();
      setLoaded(false);
      onClose();
    },
    handleModalSubmit() {
      form.validateFields().then((values) => {
        // 收集所有已选标签 ID，过滤空值并展平
        const labelIds = Object.values?.(values)?.filter((item: any) => !!item)?.flat();
        const { ...restParams } = modalData.extraParams || {};
        const params: any = {
          ...restParams,
          labelIds,
        };
        // saas目前还没迁移加盟商标签的逻辑，所以目前一定是走type = 2的逻辑，将来如果加上加盟商标签那就把这句删掉就行
        let targetFetch = updateFranchiseeLabels;
        if (type === 2) { // 商圈
          targetFetch = saveLabelRelations;
          // 将 values 中的值（单选为 number，多选为 number[]）映射为 name
          const allLabelMap = new Map<number, string>();
          labelsData.forEach((group: any) => {
            group.labels.forEach((label: any) => allLabelMap.set(label.id, label.name));
          });
          const labelNames: string[] = [];
          Object.values(values).forEach((val: any) => {
            if (val === undefined || val === null) return;
            (isArray(val) ? val : [val]).forEach((id: number) => {
              const name = allLabelMap.get(id);
              if (name) labelNames.push(name);
            });
          });
          params.labelNames = labelNames;
        }
        targetFetch(params).then(() => {
          if (type === 2) { // 商圈时才需要埋点
            const opEvent = `商圈名称：${params?.areaName || ''}，操作标签：${params.labelNames.join('、')}`;
            bigdataFn('375903f3-30ab-473f-af57-6da8c13240c9', '商圈打标', '调整标签', {
              op_event: opEvent,
              op_block: ''
            });
          }
          methods.handleModalClose();
          onSuccess && onSuccess();
        });
      });
    },
  });

  return (
    <Modal
      title='标签修改'
      open={modalData.open}
      width={480}
      onCancel={methods.handleModalClose}
      destroyOnClose
      onOk={methods.handleModalSubmit}
      bodyStyle={{
        paddingTop: '12px',
        paddingBottom: '0px',
      }}
    >
      <V2Form
        form={form}
        className={styles.labelGroupFormCon}
      >
        {
          loaded && !labelsData.length
            ? <V2Empty customTip='暂无标签数据'/>
            : labelsData?.map((item: any, index: number) => <div key={index}>
              {
                item.optionType === 1 && item?.labels?.length > 0 && <V2FormRadio
                  label={<>
                    {item.name}
                    <span className='c-666 fs-12 pl-4'>(单选)</span>
                  </>}
                  name={`group-${item.id}`}
                  canClearEmpty
                  options={
                    item?.labels?.map((labelItem: any) => ({
                      ...labelItem,
                      label: labelItem.name,
                      value: labelItem.id
                    }))
                  }
                />
              }
              {
                item.optionType === 2 && item?.labels?.length > 0 && <V2FormCheckbox
                  label={<>
                    {item.name}
                    <span className='c-666 fs-12 pl-4'>(多选)</span>
                  </>}
                  name={`group-${item.id}`}
                  options={
                    item?.labels?.map((labelItem: any) => ({
                      ...labelItem,
                      label: labelItem.name,
                      value: labelItem.id
                    }))
                  }
                />
              }
            </div>)
        }
      </V2Form>
    </Modal>
  );
};

export default SelectLabelModal;
